import { DestroyRef, Directive, inject, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { castArray } from 'lodash-es';
import { merge, Observable } from 'rxjs';
import { EmploymentAccess } from '../employment-access.model';
import { EmploymentAccessService } from '../services/employment-access.service';
import { EmploymentAccessDataFetcherService } from './../services/employment-access-data-fetcher.service';

@Directive({ standalone: true, selector: '[pr-company-employee-access]' })
export class CompanyEmployeeAccessDirective implements OnInit {
  @Input('pr-company-employee-access') set setUserId(userId: string) {
    this.userId = userId;
    this.updateView();
  }

  @Input('pr-company-employee-accessCompanyId') set setCompanyId(companyId: number) {
    this.companyId = Number(companyId);
    this.updateView();
  }

  @Input('pr-company-employee-accessEmploymentId') set setEmployeeId(employmentId: number) {
    this.employmentId = Number(employmentId);
    this.updateView();
  }

  @Input('pr-company-employee-accessPermissions') set setPermissions(permissions: string | string[]) {
    this.permissions = castArray(permissions);
    this.updateView();
  }

  @Input('pr-company-employee-accessOwnPermissions') set setOwnPermissions(ownPermissions: string | string[]) {
    this.ownPermissions = castArray(ownPermissions);
    this.updateView();
  }

  @Input('pr-company-employee-accessStrict') set setStrict(strict: boolean) {
    this.strict = strict;
    this.updateView();
  }

  @Input('pr-company-employee-accessAlternativeView') set setAlternativeView(alternativeView: TemplateRef<any>) {
    this.alternativeTemplateRef = alternativeView;
    this.updateView();
  }

  private readonly destroyRef = inject(DestroyRef);
  private companyId: number;
  private employmentId: number;
  private ownPermissions: string[] = [];
  private permissions: string[] = [];
  private strict = false;
  private userId: string;
  private alternativeTemplateRef: TemplateRef<any>;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainerRef: ViewContainerRef,
    private employmentAccessService: EmploymentAccessService,
    private employmentAccessDataFetcherService: EmploymentAccessDataFetcherService
  ) {}

  ngOnInit() {
    this.updateView();

    this.getManagedEmploymentsAccessData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateView());
  }

  private updateView() {
    if (!this.userId || !this.companyId || !this.permissions) {
      this.checkAlternativeView();

      return;
    }

    const hasAccessToAnyEmployment: boolean = this.employmentAccessService.hasAccessToAnyCompanyEmployeeEmployment(
      this.userId,
      this.companyId
    );

    this.viewContainerRef.clear();

    if (!hasAccessToAnyEmployment) {
      this.checkAlternativeView();

      return;
    }

    if (!this.permissions?.length && !this.ownPermissions.length) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);

      return;
    }

    if (
      this.employmentId &&
      this.employmentAccessService.hasPermissionsToSelectedCompanyEmployeeEmployment(
        this.permissions,
        this.ownPermissions,
        this.employmentId
      )
    ) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);

      return;
    }

    if (
      this.employmentId ||
      !this.employmentAccessService.hasPermissionsAtLeastOneEmployeeEmployment(
        this.permissions,
        this.ownPermissions,
        this.companyId,
        this.userId,
        this.strict
      )
    ) {
      this.checkAlternativeView();

      return;
    }

    this.viewContainerRef.createEmbeddedView(this.templateRef);
  }

  private getManagedEmploymentsAccessData(): Observable<EmploymentAccess[]> {
    return merge(
      this.employmentAccessDataFetcherService.managedEmploymentsAccess$,
      this.employmentAccessDataFetcherService.myEmploymentsAccess$
    );
  }

  private checkAlternativeView() {
    if (!this.alternativeTemplateRef) {
      return;
    }
    this.viewContainerRef.clear();
    this.viewContainerRef.createEmbeddedView(this.alternativeTemplateRef);
  }
}
